<template>
  <div class="app-file-preview" :class="{ 'app-file-preview--loading': loading }">
    <!-- Loading indicator -->
    <div v-if="loading" class="app-file-preview__loading">
      <div class="app-loading-spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>
    
    <!-- Error state -->
    <div v-else-if="error" class="app-file-preview__error">
      <i class="fas fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <slot name="error-actions"></slot>
    </div>
    
    <!-- Preview content based on file type -->
    <template v-else>      <!-- Image files preview -->
      <div v-if="isImage" class="app-file-preview__image">
        <img 
          :src="fileUrl" 
          :alt="fileName || ''" 
          class="app-file-preview__zoomable"
          @load="onImageLoad"
          @error="onImageError"
        />
        <div class="app-file-preview__zoom-instructions">
          <i class="fas fa-search-plus"></i>
          <span>{{ $t('documents.clickToZoom') }}</span>
        </div>
      </div>
        <!-- PDF files preview -->      <div v-else-if="isPdf" class="app-file-preview__pdf">
        <SimplePDFViewer :pdfUrl="fileUrl" />
      </div>
      
      <!-- Text files preview -->
      <div v-else-if="isTextFile" class="app-file-preview__text">
        <pre>{{ textContent }}</pre>
      </div>
      
      <!-- Other files - no preview available -->
      <div v-else class="app-file-preview__unavailable">
        <div class="app-file-preview__icon">
          <slot name="file-icon">
            <i class="fas fa-file" style="font-size: 3rem; color: #95a5a6;"></i>
          </slot>
        </div>
        <p>{{ $t('documents.noPreviewAvailable') }}</p>
        <slot name="no-preview-actions"></slot>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed } from 'vue';
// @ts-ignore
import { watch, onMounted, onUnmounted, nextTick } from 'vue';
import mediumZoom from 'medium-zoom';
import SimplePDFViewer from './SimplePDFViewer.vue';

interface Props {
  fileUrl: string;
  fileName: string;
  fileType: string;
  loading: boolean;
  error: string;
}

const props = withDefaults(defineProps<Props>(), {
  fileUrl: '',
  fileName: '',
  fileType: '',
  loading: false,
  error: ''
});

const textContent = ref<string>('');
const imageZoom = ref<any>(null);

const fileExtension = computed(() => {
  if (props.fileType) return props.fileType;
  return props.fileName ? props.fileName.split('.').pop()?.toLowerCase() : '';
});

const isImage = computed(() => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
  return imageExtensions.includes(fileExtension.value);
});

const isPdf = computed(() => {
  return fileExtension.value === 'pdf';
});

const isTextFile = computed(() => {
  const textExtensions = ['txt', 'md', 'json', 'xml', 'csv', 'html', 'css', 'js'];
  return textExtensions.includes(fileExtension.value);
});

// Load text content if it's a text file
watch(() => [props.fileUrl, isTextFile.value], async ([url, isText]: [string, boolean]) => {
  if (url && isText) {
    try {
      const response = await fetch(url);
      textContent.value = await response.text();
    } catch (error) {
      textContent.value = '';
      console.error('Failed to load text content:', error);
    }
  }
}, { immediate: true });

// Initialize zoom for images
onMounted(() => {
  if (isImage.value && props.fileUrl) {
    nextTick(() => {
      const imgElement = document.querySelector('.image-preview img') as HTMLImageElement | null;
      if (imgElement) {
        imageZoom.value = mediumZoom(imgElement, {
          background: 'rgba(0, 0, 0, 0.8)',
          margin: 20
        });
      }
    });
  }
});

// Clean up zoom instance
onUnmounted(() => {
  if (imageZoom.value) {
    imageZoom.value.detach();
  }
});

const onImageLoad = (): void => {
  console.log('Image loaded successfully');
};

const onImageError = (error: Event): void => {
  console.error('Failed to load image:', error);
};

// Watch for URL changes to reinitialize zoom
watch(() => props.fileUrl, (newUrl: string) => {
  if (newUrl && isImage.value) {
    nextTick(() => {
      if (imageZoom.value) {
        imageZoom.value.detach();
      }
      const imgElement = document.querySelector('.image-preview img') as HTMLImageElement | null;
      if (imgElement) {
        imageZoom.value = mediumZoom(imgElement, {
          background: 'rgba(0, 0, 0, 0.8)',
          margin: 20
        });
      }
    });
  }
});
</script>

<style scoped>
.app-file-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--neutral-50);
  border-radius: var(--border-radius);
  padding: var(--space-md);
  position: relative;
}

.app-file-preview--loading {
  opacity: 0.7;
}

.app-file-preview__loading,
.app-file-preview__error,
.app-file-preview__unavailable {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-xl);
  text-align: center;
  width: 100%;
  height: 100%;
}

.app-file-preview__error i {
  font-size: 3rem;
  color: var(--danger);
  margin-bottom: var(--space-md);
}

.app-loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--neutral-200);
  border-left-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-md);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.app-file-preview__image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.app-file-preview__image img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.app-file-preview__image img.app-file-preview__zoomable {
  cursor: zoom-in;
}

.app-file-preview__zoomable {
  cursor: zoom-in;
}

.app-file-preview__zoom-instructions {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.875rem;
  gap: var(--space-xs);
  margin-top: var(--space-sm);
  font-size: var(--font-size-sm);
}

.app-file-preview__zoom-instructions i {
  font-size: 0.9rem;
}

.app-file-preview__pdf {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.app-file-preview__text {
  width: 100%;
  height: 100%;
  background-color: white;
  border: 1px solid var(--neutral-200);
  border-radius: var(--border-radius);
  padding: var(--space-md);
  overflow: visible;
}

.app-file-preview__text pre {
  margin: 0;
  white-space: pre-wrap;
  font-family: monospace;
  color: var(--text-primary);
}

.app-file-preview__unavailable {
  color: var(--neutral-600);
}

.app-file-preview__icon {
  margin-bottom: var(--space-md);
}

/* For responsive design */
@media (max-width: 768px) {
  .app-file-preview {
    padding: var(--space-sm);
  }
}
</style>
